<template>
  <RequiresPermission
    permission="view_users"
    fallback-route-name="merchantIndex"
  >
    <div>
      <div
        v-if="permissions.includes('manage_users')"
        class="mb-3 flex justify-end"
      >
        <RouterLink
          #default="{ href, navigate }"
          :to="{ name: 'roleCreate' }"
          custom
        >
          <ActionButton
            as="a"
            :href="href"
            @click="navigate"
          >
            Create New Role
          </ActionButton>
        </RouterLink>
      </div>
      <BaseCard class="relative">
        <BaseTable
          :rows="data ?? []"
          :columns="columns"
          :is-loading="isLoading"
        >
          <template #cell-update="{ row }">
            <BaseTableButton
              v-if="permissions.includes('manage_users')"
              :to="{
                name: 'roleEdit',
                params: {
                  roleId: row.id,
                },
              }"
              variant="info"
            >
              Edit
            </BaseTableButton>
          </template>
          <template #cell-delete="{ row }">
            <BaseTableButton
              v-if="permissions.includes('manage_users')"
              variant="danger"
              @click="handleOpenDeleteRoleModal(row)"
            >
              Remove
            </BaseTableButton>
          </template>
        </BaseTable>
      </BaseCard>
    </div>
    <BaseConfirmation
      ref="deleteRoleConfirmationRef"
      title="Delete Role"
      confirm-label="Delete"
      cancel-label="Cancel"
    >
      <p>Are you sure you want to delete this role?</p>
      <br />
      <p
        >There are <b>{{ roleToDeleteUserCount }}</b> users
        currently assigned to this role.</p
      >
    </BaseConfirmation>
  </RequiresPermission>
</template>

<script setup lang="ts">
import { useRequiredPermissions } from "@/composables/useRequiredPermissions"
import type { TableColumn } from "@/components/BaseTable.vue"
import type { ListRolesResponseItem } from "@/services/users/listRoles"

const { permissions } = useAuth()
const { hasRequiredPermissions } =
  useRequiredPermissions("view_users")
const { getPermissionLabel } = usePermissions()

const deleteRoleConfirmationRef = useTemplateRef(
  "deleteRoleConfirmationRef"
)

const deleteIsSubmitting = ref(false)
const roleToDeleteUserCount = ref<number | null>(null)

const handleOpenDeleteRoleModal = async (
  role: ListRolesResponseItem
) => {
  if (deleteRoleConfirmationRef.value === null) return

  roleToDeleteUserCount.value = role.userCount

  const { isCanceled } =
    await deleteRoleConfirmationRef.value.reveal()

  if (isCanceled) return

  handleDeleteRole(role)
}

const handleDeleteRole = async (
  role: ListRolesResponseItem
) => {
  if (deleteIsSubmitting.value) return

  try {
    deleteIsSubmitting.value = true
    await deleteRole(role.id)
    toast.success("Role deleted")
  } catch {
    toast.error("Failed to delete user")
  } finally {
    deleteIsSubmitting.value = false
  }
}

const { data, isLoading } = useRoles(hasRequiredPermissions)

type ColumnId = "name" | "permissions" | "update" | "delete"
const columns: Array<
  TableColumn<ListRolesResponseItem, ColumnId>
> = [
  {
    id: "name",
    header: "Name",
    getCellValue: (row) => row.name,
    isSortable: false,
  },
  {
    id: "permissions",
    header: "Permissions",
    getCellValue: (row) =>
      row.permissions
        .map((permission) => getPermissionLabel(permission))
        .join(", "),
    isSortable: false,
  },
  {
    id: "update",
    header: "",
    getCellValue: () => "",
    isSortable: false,
    autoWidth: true,
  },
  {
    id: "delete",
    header: "",
    getCellValue: () => "",
    isSortable: false,
    autoWidth: true,
  },
]
</script>
